'use client';

import React, { useRef, useEffect, useState } from 'react';

interface CutsceneOverlayProps {
  videoSrc: string;
  onEnd: () => void;
  onSkip: () => void;
}

export default function CutsceneOverlay({ videoSrc, onEnd, onSkip }: CutsceneOverlayProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const [showSkip, setShowSkip] = useState(false);

  useEffect(() => {
    // Show skip button after 1.5s
    const t = setTimeout(() => setShowSkip(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {
      // Autoplay blocked or video missing — skip immediately
      setVideoError(true);
      onEnd();
    });
  }, [onEnd]);

  const handleVideoEnd = () => {
    onEnd();
  };

  const handleVideoError = () => {
    setVideoError(true);
    // Backend integration: log video_load_error with videoSrc
    // Instantly skip — no video file present, don't block gameplay
    onEnd();
  };

  return (
    <div className="absolute inset-0 z-50 bg-black cutscene-overlay">
      {!videoError ? (
        <video
          ref={videoRef}
          src={videoSrc}
          className="w-full h-full object-cover"
          playsInline
         
          onEnded={handleVideoEnd}
          onError={handleVideoError}
          // Backend integration: Replace src with actual video from /public/assets/video/
        />
      ) : (
        // Fallback — video not found (placeholder for dev)
        <div className="w-full h-full flex flex-col items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1a0a2e 100%)' }}
        >
          <div className="flex flex-col items-center gap-4">
            {/* Animated logo placeholder */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: 'radial-gradient(circle, rgba(0,229,255,0.2) 0%, transparent 70%)',
                border: '2px solid rgba(0,229,255,0.4)',
                animation: 'permissionPulse 2s ease-in-out infinite',
              }}
            >
              <svg width="40" height="40" viewBox="0 0 80 80" fill="none">
                <rect x="10" y="60" width="60" height="10" rx="5" fill="#ff4d6d" />
                <circle cx="22" cy="72" r="6" fill="#00e5ff" />
                <circle cx="58" cy="72" r="6" fill="#00e5ff" />
                <circle cx="40" cy="24" r="12" fill="rgba(0,229,255,0.6)" />
                <rect x="28" y="36" width="24" height="26" rx="5" fill="rgba(0,229,255,0.5)" />
              </svg>
            </div>
            <p
              className="text-lg font-bold"
              style={{ color: '#00e5ff', fontFamily: 'DM Sans, sans-serif' }}
            >
              Italy Night Circuit
            </p>
            <p
              className="text-sm text-center max-w-xs"
              style={{ color: '#8899aa', fontFamily: 'DM Sans, sans-serif' }}
            >
              Drop your intro video at<br />
              <code className="text-[#00e5ff] text-xs">/public/assets/video/intro_italy.mp4</code>
            </p>
          </div>
        </div>
      )}

      {/* Skip button */}
      {showSkip && (
        <button
          className="absolute bottom-8 right-6 btn-action fade-in-fast"
          style={{
            background: 'rgba(13,13,15,0.72)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '10px 18px',
          }}
          onClick={onSkip}
          onTouchStart={e => { e.preventDefault(); onSkip(); }}
        >
          <span
            className="text-sm font-bold tracking-widest uppercase"
            style={{ color: '#8899aa', fontFamily: 'DM Sans, sans-serif' }}
          >
            Skip ▶
          </span>
        </button>
      )}

      {/* Letterbox bars */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{ height: '6%', background: '#000' }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none"
        style={{ height: '6%', background: '#000' }}
      />
    </div>
  );
}